import React, { useEffect, useState } from 'react';
import { Flex } from 'antd';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createSelector } from 'reselect';

import { APIClient } from '../../helpers/api_helper';
import VehicleDistributionMap from './VehicleDistributionMap';
import Widget from './Widget';
import Top5AlarmTypesByCount from './Top5AlarmTypesByDailyCount';
import DailyOnlineVehicleAndAlarms from './DailyOnlineVehicleAndAlarms';
import LastestStatus from '../../components/LastestStatus/LastestStatus';
import Top5VehiclesByDailyAlarmsCount from './Top5VehiclesByDailyAlarmsCount';
import Top5ProjectsByDailyAbnormalStartups from './Top5ProjectsByDailyAbnormalStartups';
import '../../assets/scss/_scroll.scss';
import VehicleListModel from '../../Common/VehicleListModel';
import * as VThunk from '../../slices/vehicles/thunk';

const Vehicles = () => {
    document.title = '车辆统计' + process.env.REACT_APP_PAGE_TITLE;
    const user = useSelector(state => state.Login.user);
    const selector = createSelector(
        state => state.Vehicles,
        state => ({
            dataList: state.dataList,
            loading: state.loading,
        }),
    );
    const { dataList, loading } = useSelector(selector);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    /**
     * 显示车辆详细列表弹窗
     */
    const [showVehicleListModel, setShowVehicleListModel] = useState(false);

    /**
     * 卡片类型
     */
    const [cardType, setCardType] = useState('');

    /**
     * 车辆详细列表数据
     */
    const [vehicleListData, setVehicleListData] = useState([]);

    /**
     * 车辆详细列表标题
     */
    const [title, setTitle] = useState({});

    useEffect(() => {
        dispatch(VThunk.getAll());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 提取获取日期范围的公共函数
    const getDateRange = () => {
        const startDate = moment().startOf('day').format('YYYY-MM-DD');
        const endDate = moment().format('YYYY-MM-DD');
        return { startDate, endDate };
    };

    // 提取根据角色处理 API 请求的公共函数
    const handleApiRequest = async (id, endpoints) => {
        const { startDate, endDate } = getDateRange();
        const role = user?.user?.role;
        try {
            let data;
            if (role === 'SystemAdmin' || role === 'Admin' || role === 'Supervisor') {
                const response = await APIClient.get(`${endpoints.systemAdmin}?startDate=${startDate}&endDate=${endDate}&projectId=${id}`);
                data = response.data;
            } else if (role === 'ProjectAdmin') {
                const response = await APIClient.get(`${endpoints.projectAdmin}?startDate=${startDate}&endDate=${endDate}&siteId=${id}`);
                data = response.data;
            } else if (role === 'ProjectSupervisor') {
                dispatch(navigate(`/vehicles/${id}?tab=location`));
                return null;
            }
            return data || [];
        } catch (error) {
            return {};
        }
    };

    async function getIdByAlarmsCount(id, title) {
        const endpoints = {
            systemAdmin: '/statistics/vehicle-alarm-count/vehicle',
            projectAdmin: '/statistics/vehicle-alarm-count/vehicle'
        };
        const data = await handleApiRequest(id, endpoints);
        setVehicleListData(data);
        setTitle(title);
        setCardType('AlarmsCount');
        setShowVehicleListModel(true);
    }

    async function getIdByDailyOnline(id, title) {
        const endpoints = {
            systemAdmin: '/statistics/vehicle-online-duration/vehicle',
            projectAdmin: '/statistics/vehicle-online-duration/vehicle'
        };
        const data = await handleApiRequest(id, endpoints);
        setVehicleListData(data);
        setTitle(title);
        setCardType('DailyOnline');
        setShowVehicleListModel(true);
    }

    async function getIdByAbnormalStart(id, title) {
        const endpoints = {
            systemAdmin: '/statistics/vehicle-abnormal-start',
            projectAdmin: '/statistics/vehicle-abnormal-start'
        };
        const role = user?.user?.role;
        const data = role === 'Admin' 
            ? await APIClient.get(`/statistics/vehicle-abnormal-start?startDate=${getDateRange().startDate}&endDate=${getDateRange().endDate}&companyId=${id}`).then(res => res.data || [])
            : await handleApiRequest(id, endpoints);
        setVehicleListData(data);
        setTitle(title);
        setCardType('AbnormalStart');
        setShowVehicleListModel(true);
    }

    const closeModel = () => {
        setShowVehicleListModel(false);
        setVehicleListData({});
    };

    return (
        <Flex gap="middle" vertical align="stretch" justify='space-between' style={{ height:'calc(100vh - 142px)'}}>
            {/* 顶部的简要统计信息 */}
            <Widget vehicleListData={dataList} loading={loading} style={{ height:'32%', minHeight: '200px'}}/>

            {/* TOPS 统计 + map */}
            <Flex gap="middle" align="stretch" justify="stretch" style={{ height:'80%', minHeight: '400px', paddingBottom:'1em'}}>
                <Flex gap="middle" align="stretch" vertical justify='space-between' style={{ width:'20%', minWidth: 0 }}>
                    <Top5AlarmTypesByCount style={{ height: 'calc(50% - 8px)', minHeight: '200px' }} vehicleListData={dataList}/>  
                    <Top5VehiclesByDailyAlarmsCount style={{ height: 'calc(50% - 8px)', minHeight: '200px' }} getIdByAlarmsCount={getIdByAlarmsCount} vehicleListData={dataList}/>
                </Flex>
                <Flex gap="middle" align="stretch" vertical justify='space-between' style={{ width:'60%', minWidth: 0 }}>
                    <VehicleDistributionMap style={{ height: 'calc(50% - 8px)', minHeight: '200px' }} />
                    <DailyOnlineVehicleAndAlarms style={{ height: 'calc(50% - 8px)', minHeight: '200px' }} />     
                </Flex>
                <Flex gap="middle" align="stretch" vertical justify='space-between' style={{ width:'20%', minWidth: 0 }}>
                    <Top5ProjectsByDailyAbnormalStartups style={{ height: 'calc(50% - 8px)', minHeight: '200px' }} getIdByAbnormalStart={getIdByAbnormalStart} vehicleListData={dataList}/>
                    <LastestStatus style={{ height: 'calc(50% - 8px)', minHeight: '200px' }} />
                </Flex>
            </Flex>
            <VehicleListModel
                cardType={cardType}
                title={title}
                visible={showVehicleListModel}
                data={vehicleListData}
                close={closeModel}
            ></VehicleListModel>
        </Flex>
    );
};

export default Vehicles;